import { useModalState } from "@src/lib/ui-react";
import { Stack } from "@mui/material";
import { RecentlyVisitedPage, useRecentlyVisited } from "@src/appV2/lib/utils/useRecentlyVisited";

import { NavigationMenuSectionTitleDesktop } from "../../../../../appV2/redesign/AppLayout/Desktop/Navigation/MenuSectionTitle";
import { useAccountSwitcher } from "../../Shared/AccountSwitcher/useAccountSwitcher";
import { Button } from "@src/appV2/redesign/components/Button";
import { RecentlyViewedDialog } from "@src/appV2/redesign/AppLayout/Desktop/RecentlyViewedDialog";
import { useIsRebrand } from "@src/appV2/redesign/theme/RebrandProvider";

// Default number of items to show in the UI
export const CONFIG_MAX_VISIBLE_ITEMS = 3;

interface RecentlyViewedSectionDesktopProps {
  admin: boolean;
}

export function RecentlyViewedSectionDesktop(props: RecentlyViewedSectionDesktopProps) {
  const { admin } = props;

  const [recentVisited] = useRecentlyVisited();
  const { onUserClicked } = useAccountSwitcher();
  const recentlyViewedDialogState = useModalState();
  const isRebrand = useIsRebrand();

  // Only show for admin users
  if (!admin) {
    return null;
  }

  // Don't show if there are no recently visited items
  if (!recentVisited || recentVisited.length === 0) {
    return null;
  }

  const visibleItems = recentVisited.slice(0, CONFIG_MAX_VISIBLE_ITEMS);

  const hasMoreItems = recentVisited.length > CONFIG_MAX_VISIBLE_ITEMS;

  return (
    <>
      <NavigationMenuSectionTitleDesktop>Recently Viewed</NavigationMenuSectionTitleDesktop>
      <Stack gap={2}>
        {visibleItems.map((item) => (
          <Button
            key={item.id}
            variant={isRebrand ? "outlined" : "outlineInverted"}
            onClick={() => {
              onUserClicked({
                userId: item.id,
                name: item.title,
                title: item.title,
                type: item.type as "AGENT" | "WORKPLACE",
              });
            }}
            endIconType="hyperlink"
            sx={(theme) => ({
              height: "auto",
              ...(isRebrand && {
                backgroundColor: theme.palette.background?.baseDark,
                color: theme.palette.base?.strong,
                "& .MuiButton-endIcon": {
                  color: theme.palette.icon?.muted,
                },
              }),
            })}
          >
            {item.title}
          </Button>
        ))}

        {hasMoreItems && (
          <Button
            variant={isRebrand ? "link" : "linkInverted"}
            size="small"
            onClick={recentlyViewedDialogState.openModal}
            sx={(theme) => ({
              ...(isRebrand && { color: theme.palette.base?.muted }),
            })}
          >
            View More
          </Button>
        )}

        {
          <RecentlyViewedDialog
            modalState={recentlyViewedDialogState}
            recentVisited={recentVisited}
            onUserClicked={(item: RecentlyVisitedPage) => {
              onUserClicked({
                userId: item.id,
                name: item.title,
                title: item.title,
                type: item.type as "AGENT" | "WORKPLACE",
              });
            }}
          />
        }
      </Stack>
    </>
  );
}
